import {
	Cancel,
	CastForEducation,
	CastForEducationOutlined,
	CheckCircle,
	ChevronRight,
	ContentCopy,
	MenuBook,
	MenuBookOutlined,
	PaidOutlined,
	PostAdd,
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import Person2OutlinedIcon from "@mui/icons-material/Person2Outlined";
import { Box, Button, ClickAwayListener, Divider, Menu, MenuItem } from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useDraftMode } from "../../contexts/DraftModeContext";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { type HospitalDataContextType, useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSelfScheduling } from "../../contexts/SelfScheduling/SelfSchedulingContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { NURSE_PROFILE_QUERY_KEY, useInvalidateNurseQueries } from "../../hooks/useInvalidateNurseQueries";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useShiftFlagActions } from "../../hooks/useShiftFlagActions";
import { type ChangesSource, type DayOff, type NurseInShift, NurseTrainingStatus } from "../../utils/models";
import { daysOffAndPtoToNursePreferences } from "../../utils/utilFunctions";
import AddDayOffDialog from "../AddDayOffDialog/AddDayOffDialog";
import { DailyGapsQuickAction } from "../DayViewGaps/DailyRow/NurseInShiftTrailingNode/utils";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { DeleteRecurringOrSingleShift } from "../NormalizedNurseMenu/DeleteRecurringOrSingleShift/DeleteRecurringOrSingleShift";
import { PostNoteDialog } from "../PostNoteDialog/PostNoteDialog";
import { RecurringShiftPanelWrapper } from "../RecurringShift/RecurringShiftPanelWrapper";
import { RecurringShiftDisplayMode } from "../RecurringShift/util/useRecurringShiftState";
import { ShiftHistoryDrawer } from "../ShiftHistory/ShiftHistoryDrawer";
import { minimalWeekIndexThatIsDraft } from "../staffing/DraftMode";
import { SystemValidationTextField } from "../UIToolkit/Inputs/SystemValidationTextField";
import { MenuItemWithReason } from "../UIToolkit/Popups/MenuItemWithReason";
import { ThemedSvg } from "../UIToolkit/ThemedSvg";
import { useBonusMenu } from "./hooks/useBonusMenu";

const recurringShiftMenuItemText = "Edit Recurring Shift";
const removeText = "Remove";
// Standard 8px (MUI theme spacing unit 1) gap between every menu-item icon and its label in this file.
const MENU_ITEM_ICON_GAP = 1;

const ChargeMenuContent = ({ isCharge }: { isCharge: boolean }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: MENU_ITEM_ICON_GAP,
		}}
	>
		<ThemedSvg src="/plus-medical.svg" alt="Make charge" />
		{isCharge ? "Relieve Charge" : "Make Charge"}
	</Box>
);

const TraineeMenuContent = ({ isTrainee }: { isTrainee: boolean }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: MENU_ITEM_ICON_GAP,
		}}
	>
		{isTrainee ? (
			<>
				<ThemedSvg src="/student.svg" alt="Remove Trainee" size={20} />
				Remove Trainee
			</>
		) : (
			<>
				<ThemedSvg src="/student.svg" alt="Mark Trainee" size={20} />
				Mark Trainee
			</>
		)}
	</Box>
);

const OrientationMenuContent = ({ isOrientation }: { isOrientation: boolean }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: MENU_ITEM_ICON_GAP,
		}}
	>
		{isOrientation ? (
			<>
				<MenuBookOutlined sx={{ width: "20px", height: "20px" }} />
				Remove Orientation
			</>
		) : (
			<>
				<MenuBook sx={{ width: "20px", height: "20px" }} />
				Mark Orientation
			</>
		)}
	</Box>
);

const EducationMenuContent = ({ isEducation }: { isEducation: boolean }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: MENU_ITEM_ICON_GAP,
		}}
	>
		{isEducation ? (
			<>
				<CastForEducationOutlined sx={{ width: "20px", height: "20px" }} />
				Remove Education
			</>
		) : (
			<>
				<CastForEducation sx={{ width: "20px", height: "20px" }} />
				Mark Education
			</>
		)}
	</Box>
);

const NurseBonusMenuContent = ({ nurseInShift }: { nurseInShift: NurseInShift }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: MENU_ITEM_ICON_GAP,
		}}
	>
		<PaidOutlined />
		{nurseInShift.bonus ? "Edit" : "Add"} Bonus
	</Box>
);

type Props = {
	nurseInShift: NurseInShift;
	shiftNo: number;
	dayNo: number;
	specificWeekIndex?: number;
	handleAcceptChanges: any;
	onSwapClick: any;
	handleDeclineChanges: any;
	actionsMenuAnchorEl: any;
	onMenuClose: (shiftDeleted: boolean) => void;
	showActual?: boolean;
	source: ChangesSource;
	displayProfileButton?: boolean;
	allowEditLocationAndHoursMenuItem?: React.ReactElement;
	/**
	 * Will automatically open the relevant quick action
	 */
	currentQuickAction?: DailyGapsQuickAction;
};

/**
 * @deprecated - use `NormalizedNurseMenu` instead - it wraps this component with minimal required props
 */
export const NurseMenu = ({
	nurseInShift,
	shiftNo,
	dayNo,
	handleAcceptChanges,
	onSwapClick,
	onMenuClose,
	handleDeclineChanges,
	actionsMenuAnchorEl,
	showActual,
	source,
	displayProfileButton = true,
	specificWeekIndex,
	allowEditLocationAndHoursMenuItem,
	currentQuickAction,
}: Props) => {
	const { t } = useTranslation();
	const { isImportingSelfScheduling } = useSelfScheduling();
	const {
		isAutoScheduler,
		isHospital,
		getShiftDate,
		showErrorSnackbar,
		isHospitalDemo,
		isSnfDemo,
		chosenDepartment,
		updateShiftBonus,
		updateNurse: updateNurseInHospitalData,
		getNurseById,
		getNurseFormattedName,
	}: HospitalDataContextType = useHospitalData();
	const navigate = useNavigate();
	const { weekIndex: initialWeekIndex } = useWeekIndex();
	const weekIndex = typeof specificWeekIndex === "number" ? specificWeekIndex : initialWeekIndex;
	const { updateNurse } = useHandleNurseChange();
	const invalidateNurseQueries = useInvalidateNurseQueries();
	const { toggleCharge, toggleTrainingStatus, isChargeSaving, isTrainingStatusSaving } = useShiftFlagActions({
		nurseInShift,
		weekIndex,
		shiftNo,
		dayNo,
		showActual,
		analyticsSource: "nurse_menu",
	});
	const { track } = useAnalytics();
	const { isDraftMode } = useDraftMode();
	const { userInfo } = useAuth();
	const { copyToClipboard } = useCopyToClipboard();

	const { isPending, isPendingToDelete } = nurseInShift;

	// Track when the menu is opened
	useEffect(() => {
		if (actionsMenuAnchorEl) {
			track("nurse actions menu opened");
		}
	}, [actionsMenuAnchorEl, track]);

	const [showRecurringShift, setShowRecurringShift] = useState<boolean>(false);
	const [showDelete, setShowDelete] = useState<boolean>(false);
	const [showShiftHistory, setShowShiftHistory] = useState<boolean>(false);
	const { bonusMenuAnchorEl, setBonusMenuAnchorEl, bonusValue, setBonusValue } = useBonusMenu({ initValue: nurseInShift.bonus });
	const { showSnackbar } = useSnackbar();
	const [addPtoOpen, setAddPtoOpen] = useState<boolean>(false);
	const [postNoteOpen, setPostNoteOpen] = useState<boolean>(false);
	const [copyMenuAnchorEl, setCopyMenuAnchorEl] = useState<null | HTMLElement>(null);

	const isRecurring = useMemo(() => nurseInShift.recurringShiftId !== undefined && !!nurseInShift.recurringShiftId, [nurseInShift.recurringShiftId]);

	const handleToggleShowRecurringShift = useCallback(() => {
		setShowRecurringShift((prevState) => !prevState);
	}, []);

	const handleToggleShowDelete = useCallback(() => {
		setShowDelete((prevState) => !prevState);
	}, []);

	const onDeleteCancel = useCallback(() => {
		handleToggleShowDelete();
		if (currentQuickAction) {
			// If we came from a quick action, we should close the menu on cancel
			onMenuClose(false);
		}
	}, [handleToggleShowDelete, onMenuClose, currentQuickAction]);

	const handleRecurringShiftClose = useCallback(() => {
		handleToggleShowRecurringShift();
		onMenuClose(false);
	}, [handleToggleShowRecurringShift, onMenuClose]);

	const handleCopyNurseInShiftId = useCallback(async () => {
		const success = await copyToClipboard(nurseInShift.id);
		if (success) {
			showSnackbar("Shift ID copied to clipboard", "success");
		} else {
			showErrorSnackbar("Failed to copy to clipboard", {});
		}
		setCopyMenuAnchorEl(null);
		onMenuClose(false);
	}, [nurseInShift.id, copyToClipboard, showSnackbar, showErrorSnackbar, onMenuClose]);

	const handleCopyNurseId = useCallback(async () => {
		const success = await copyToClipboard(nurseInShift.nurseId);
		if (success) {
			showSnackbar("Nurse ID copied to clipboard", "success");
		} else {
			showErrorSnackbar("Failed to copy to clipboard", {});
		}
		setCopyMenuAnchorEl(null);
		onMenuClose(false);
	}, [nurseInShift.nurseId, copyToClipboard, showSnackbar, showErrorSnackbar, onMenuClose]);

	const handleCopyMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setCopyMenuAnchorEl(event.currentTarget);
	}, []);

	const handleCopyMenuClose = useCallback(() => {
		setCopyMenuAnchorEl(null);
	}, []);

	const handlePtoSave = useCallback(
		(dayOff: DayOff) => {
			const nurse = getNurseById(nurseInShift.nurseId);
			if (nurse) {
				updateNurseInHospitalData({
					...nurse,
					employeeSpecificPreferences: [...nurse.employeeSpecificPreferences, ...daysOffAndPtoToNursePreferences([dayOff], nurse)],
				});
			}
			invalidateNurseQueries(NURSE_PROFILE_QUERY_KEY);
			setAddPtoOpen(false);
			onMenuClose(false);
		},
		[onMenuClose, getNurseById, nurseInShift.nurseId, updateNurseInHospitalData, invalidateNurseQueries],
	);

	const handleAddPtoClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			setAddPtoOpen(true);
			track("nurse_menu_add_pto_clicked", {
				source: "nurse_menu",
				nurse_id: nurseInShift.nurseId,
				shift_no: shiftNo,
				day_no: dayNo,
				date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
			});
		},
		[nurseInShift.nurseId, shiftNo, dayNo, weekIndex, getShiftDate, track],
	);

	const handlePtoClose = useCallback(() => {
		setAddPtoOpen(false);
		onMenuClose(false);
	}, [onMenuClose]);

	const handlePostNoteClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			setPostNoteOpen(true);
			track("nurse_menu_post_note_clicked", {
				source: "nurse_menu",
				nurse_id: nurseInShift.nurseId,
				shift_no: shiftNo,
				day_no: dayNo,
				date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
			});
		},
		[nurseInShift.nurseId, shiftNo, dayNo, weekIndex, getShiftDate, track],
	);

	const handlePostNoteClose = useCallback(() => {
		setPostNoteOpen(false);
		onMenuClose(false);
	}, [onMenuClose]);

	const handlePostNoteSave = useCallback(() => {
		setPostNoteOpen(false);
		onMenuClose(false);
	}, [onMenuClose]);

	const handleActionsMenuClose = useCallback(
		(event: React.MouseEvent | MouseEvent | TouchEvent) => {
			event.stopPropagation();
			if (showRecurringShift || showDelete || bonusMenuAnchorEl || addPtoOpen || postNoteOpen || copyMenuAnchorEl || showShiftHistory) {
				return;
			}
			onMenuClose?.(false);
		},
		[onMenuClose, showRecurringShift, showDelete, bonusMenuAnchorEl, addPtoOpen, postNoteOpen, copyMenuAnchorEl, showShiftHistory],
	);

	const handleShiftHistoryClick = useCallback(
		(event: React.MouseEvent<HTMLLIElement>) => {
			event.stopPropagation();
			track("nurse_menu_shift_history_clicked", { source, nurse_id: nurseInShift.nurseId, shift_id: nurseInShift.id });
			setShowShiftHistory(true);
		},
		[nurseInShift.id, nurseInShift.nurseId, source, track],
	);

	// The history's "Edit Recurring Shift" swaps the drawer for the recurring editor this menu already owns.
	const handleShiftHistoryEditRecurring = useCallback(() => {
		setShowShiftHistory(false);
		setShowRecurringShift(true);
	}, []);

	const handleShiftHistoryClose = useCallback(() => {
		setShowShiftHistory(false);
		onMenuClose(false);
	}, [onMenuClose]);

	const handleBonusClick = (event: React.MouseEvent<HTMLElement>): void => {
		event.stopPropagation();
		setBonusMenuAnchorEl(event.currentTarget);
	};

	const handleBonusMenuClose = (): void => {
		setBonusMenuAnchorEl(undefined);
		setBonusValue(nurseInShift.bonus?.toString() || "");
	};

	const handleBonusSave = async (): Promise<void> => {
		const bonusAmount = bonusValue ? parseFloat(bonusValue) : undefined;

		track("Nurse Menu - update nurse bonus", {
			source: "nurse_menu",
			nurse_id: nurseInShift.nurseId,
			bonus_amount: bonusAmount,
		});

		try {
			await updateNurse({
				nurse: { ...nurseInShift, bonus: bonusAmount },
				shiftIndex: shiftNo,
				dayNo,
				weekIndex,
				isActual: showActual,
			});

			updateShiftBonus(nurseInShift.id, weekIndex, shiftNo, dayNo, bonusAmount || 0, !!showActual, isDraftMode);

			showSnackbar(t(bonusAmount ? "bonus.updated.success" : "bonus.removed.success"), "success");
			setBonusMenuAnchorEl(undefined);
			onMenuClose(false);
		} catch (error) {
			showErrorSnackbar("Failed to update nurse bonus", { nurseId: nurseInShift.id }, error);
		}
	};

	const handleDeleteBonus = async (): Promise<void> => {
		track("Nurse Menu - delete nurse bonus", {
			source: "nurse_menu",
			nurse_id: nurseInShift.nurseId,
		});

		try {
			await updateNurse({
				nurse: { ...nurseInShift, bonus: undefined },
				shiftIndex: shiftNo,
				dayNo,
				weekIndex,
				isActual: showActual,
			});

			updateShiftBonus(nurseInShift.id, weekIndex, shiftNo, dayNo, 0, !!showActual, isDraftMode);

			showSnackbar(t("bonus.removed.success"), "success");
			setBonusMenuAnchorEl(undefined);
			onMenuClose(false);
		} catch (error) {
			showErrorSnackbar("Failed to remove nurse bonus", { nurseId: nurseInShift.id }, error);
		}
	};

	const handleDeleteClick = useCallback(
		(event?: React.MouseEvent) => {
			track("delete_nurse_clicked");
			event?.stopPropagation();
			handleToggleShowDelete();
		},
		[handleToggleShowDelete, track],
	);

	const didHandleQuickActionRef = useRef<boolean>(false);

	useEffect(() => {
		if (currentQuickAction && !didHandleQuickActionRef.current) {
			didHandleQuickActionRef.current = true;
			if (currentQuickAction === DailyGapsQuickAction.EDIT_RECURRING && !chosenDepartment.disable_edit_master_schedule) {
				handleToggleShowRecurringShift();
			} else if (currentQuickAction === DailyGapsQuickAction.REMOVE) {
				handleDeleteClick();
			}
		}
	}, [currentQuickAction, handleDeleteClick, handleToggleShowRecurringShift]);

	const isShiftBonusesEnabled = useFeatureFlag(FEATURE_FLAGS.EnableShiftBonuses);

	// Independently-derived visibility flags for each menu group, used only to decide whether a
	// divider between two groups should render (never derived from the rendered JSX itself).
	const isPendingActionAllowed = isPending && (weekIndex < minimalWeekIndexThatIsDraft || (!isSnfDemo && !isHospitalDemo));
	const showApproveChange = isPendingActionAllowed && !isAutoScheduler && !isImportingSelfScheduling;
	const showDiscardChange = isPendingActionAllowed && (isAutoScheduler || (isImportingSelfScheduling && isPendingToDelete));
	const hasGroup1Item = showApproveChange || showDiscardChange;

	const showProfile = displayProfileButton;
	const showEditShiftDetails = !isPendingToDelete && !!allowEditLocationAndHoursMenuItem;
	const showEditRecurring = isRecurring;
	const showSwap = !isPending && isHospital && onSwapClick !== undefined;
	// Worked (actual) shifts have their own sync history; this one is for the schedule.
	const showShiftHistoryItem = !showActual;
	const hasGroup2Item = showProfile || showEditShiftDetails || showShiftHistoryItem || showEditRecurring || showSwap;

	const showRemove = !isPendingToDelete;

	return (
		<ClickAwayListener onClickAway={handleActionsMenuClose}>
			<div>
				{!currentQuickAction && (
					<Menu
						anchorEl={actionsMenuAnchorEl}
						// Hidden, not closed, under the history drawer and the recurring editor: menus stack above both, and
						// closing would unmount them.
						open={Boolean(actionsMenuAnchorEl) && !showShiftHistory && !showRecurringShift}
						onClose={handleActionsMenuClose}
						onClick={handleActionsMenuClose}
						anchorOrigin={{
							vertical: "bottom",
							horizontal: "right",
						}}
						transformOrigin={{
							vertical: "top",
							horizontal: "right",
						}}
						slotProps={{
							paper: {
								sx: {
									mt: 1,
								},
							},
						}}
					>
						{/* Group 1: Pending Actions */}
						{showApproveChange && (
							<MenuItem
								onClick={() => {
									track("accept schedule change clicked");
									handleAcceptChanges();
								}}
							>
								<CheckCircle sx={{ mr: MENU_ITEM_ICON_GAP }} />
								Approve change
							</MenuItem>
						)}
						{showDiscardChange && (
							<MenuItem
								onClick={() => {
									track("decline schedule change clicked", {
										is_auto_scheduler: isAutoScheduler,
									});
									handleDeclineChanges(isAutoScheduler);
								}}
							>
								<Cancel sx={{ mr: MENU_ITEM_ICON_GAP }} />
								{isAutoScheduler || isImportingSelfScheduling ? "Discard change" : "Decline change"}
							</MenuItem>
						)}
						{hasGroup1Item && <Divider sx={{ my: 1 }} />}

						{/* Group 2: Shift Actions */}
						{displayProfileButton && (
							<MenuItem
								onClick={(e) => {
									e.stopPropagation();
									const path = `/${chosenDepartment.department_id}/nurse/${nurseInShift.nurseId}`;
									navigate(path, {
										state: { navigationSrc: window.location.pathname },
									});
								}}
							>
								<Person2OutlinedIcon fontSize={"small"} sx={{ mr: MENU_ITEM_ICON_GAP }} /> Profile
							</MenuItem>
						)}
						{!isPendingToDelete && allowEditLocationAndHoursMenuItem}
						{showShiftHistoryItem && (
							<MenuItemWithReason
								disabledReason={isDraftMode ? t("shiftHistory.menu.draftDisabled") : undefined}
								onClick={handleShiftHistoryClick}
								dataTestId="nurse-menu-shift-history"
							>
								<HistoryRoundedIcon fontSize={"small"} sx={{ mr: MENU_ITEM_ICON_GAP }} />
								{t("shiftHistory.menu")}
							</MenuItemWithReason>
						)}
						{isRecurring && (
							<InHouseTooltip
								title={chosenDepartment.disable_edit_master_schedule ? t("schedule.template.edit.blocked") : ""}
								disableHoverListener={!chosenDepartment.disable_edit_master_schedule}
							>
								<span>
									<MenuItem
										onClick={(e) => {
											e.stopPropagation();
											handleToggleShowRecurringShift();
										}}
										disabled={chosenDepartment.disable_edit_master_schedule}
									>
										<EventRepeatIcon fontSize={"small"} sx={{ mr: MENU_ITEM_ICON_GAP }} />
										{recurringShiftMenuItemText}
									</MenuItem>
								</span>
							</InHouseTooltip>
						)}
						{showSwap && (
							<MenuItem
								onClick={(e) => {
									e.stopPropagation();
									track("swap nurse clicked");
									onSwapClick();
								}}
							>
								<ThemedSvg src="/swap-nurse.svg" alt="Swap nurse" sx={{ mr: MENU_ITEM_ICON_GAP }} />
								Swap nurse
							</MenuItem>
						)}
						{hasGroup2Item && <Divider sx={{ my: 1 }} />}

						{/* Group 3: Status */}
						<MenuItem disabled={isTrainingStatusSaving} onClick={() => toggleTrainingStatus(NurseTrainingStatus.Trainee)}>
							<TraineeMenuContent isTrainee={nurseInShift.trainingStatus === NurseTrainingStatus.Trainee} />
						</MenuItem>
						<MenuItem disabled={isChargeSaving} onClick={toggleCharge}>
							<ChargeMenuContent isCharge={nurseInShift.charge} />
						</MenuItem>
						<MenuItem disabled={isTrainingStatusSaving} onClick={() => toggleTrainingStatus(NurseTrainingStatus.Orientation)}>
							<OrientationMenuContent isOrientation={nurseInShift.trainingStatus === NurseTrainingStatus.Orientation} />
						</MenuItem>
						<MenuItem disabled={isTrainingStatusSaving} onClick={() => toggleTrainingStatus(NurseTrainingStatus.Education)}>
							<EducationMenuContent isEducation={nurseInShift.trainingStatus === NurseTrainingStatus.Education} />
						</MenuItem>
						<Divider sx={{ my: 1 }} />

						{/* Group 4: Extras */}
						<MenuItem onClick={handleAddPtoClick}>
							<ThemedSvg src="/day-off.svg" alt="Day off" size={17} sx={{ mr: MENU_ITEM_ICON_GAP }} />
							Add PTO
						</MenuItem>
						<MenuItem onClick={handlePostNoteClick}>
							<PostAdd fontSize={"small"} sx={{ mr: MENU_ITEM_ICON_GAP }} />
							Post Note
						</MenuItem>
						{isShiftBonusesEnabled && (
							<MenuItem onClick={handleBonusClick}>
								<NurseBonusMenuContent nurseInShift={nurseInShift} />
							</MenuItem>
						)}
						{userInfo.support && (
							<MenuItem onClick={handleCopyMenuOpen}>
								<ContentCopy fontSize={"small"} sx={{ mr: MENU_ITEM_ICON_GAP }} />
								Copy
								<ChevronRight fontSize={"small"} sx={{ marginLeft: "auto" }} />
							</MenuItem>
						)}
						{showRemove && <Divider sx={{ my: 1 }} />}

						{/* Group 5: Remove */}
						{showRemove && (
							<MenuItem key={"remove-nurse"} data-testid="nurse-menu-remove" onClick={handleDeleteClick}>
								<DeleteIcon sx={{ mr: MENU_ITEM_ICON_GAP, color: "inherint" }} />
								{removeText}
							</MenuItem>
						)}
					</Menu>
				)}
				{showRecurringShift && (
					<RecurringShiftPanelWrapper
						shouldAddNewShiftOnOpen={false}
						disableHidePopup={true}
						clickedDate={getShiftDate(weekIndex, shiftNo, dayNo)}
						clickedNurseId={nurseInShift.nurseId}
						onClose={handleRecurringShiftClose}
						existingRecurringShiftId={nurseInShift.recurringShiftId ?? undefined}
						renderWithoutModal={false}
						recurringShiftDisplayMode={RecurringShiftDisplayMode.Edit}
						existingShiftStartTime={nurseInShift.startTime}
						isSynergyMS={false}
					/>
				)}
				{showShiftHistoryItem && (
					<ShiftHistoryDrawer
						open={showShiftHistory}
						onClose={handleShiftHistoryClose}
						nurseInShift={nurseInShift}
						nurseName={getNurseFormattedName(nurseInShift.nurseId)}
						onEditRecurring={isRecurring && !chosenDepartment.disable_edit_master_schedule ? handleShiftHistoryEditRecurring : undefined}
						surface={source}
					/>
				)}
				{showDelete && (
					<DeleteRecurringOrSingleShift
						nurseInShift={nurseInShift}
						onFinished={onMenuClose}
						onCancel={onDeleteCancel}
						source={source}
						showActual={showActual ?? false}
					/>
				)}

				<Menu
					anchorEl={copyMenuAnchorEl}
					open={!!copyMenuAnchorEl}
					onClose={handleCopyMenuClose}
					anchorOrigin={{ vertical: "top", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "left" }}
				>
					<MenuItem onClick={handleCopyNurseInShiftId}>
						<ContentCopy fontSize={"small"} sx={{ mr: MENU_ITEM_ICON_GAP }} />
						Copy Shift ID
					</MenuItem>
					<MenuItem onClick={handleCopyNurseId}>
						<ContentCopy fontSize={"small"} sx={{ mr: MENU_ITEM_ICON_GAP }} />
						Copy Nurse ID
					</MenuItem>
				</Menu>

				<Menu
					anchorEl={bonusMenuAnchorEl}
					open={!!bonusMenuAnchorEl}
					onClose={handleBonusMenuClose}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					slotProps={{
						paper: {
							sx: {
								mt: 1,
								p: 2,
								minWidth: 250,
							},
						},
					}}
				>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						<SystemValidationTextField
							label="Bonus Amount ($)"
							inputType="number"
							variant="outlined"
							value={bonusValue}
							onChange={(e) => setBonusValue(e.target.value)}
							maxValue={1000}
							defaultHelperText="Enter bonus amount"
						/>
						<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
							<Button size="small" onClick={handleBonusMenuClose}>
								Cancel
							</Button>
							{!!nurseInShift.bonus && (
								<Button size="small" onClick={handleDeleteBonus} color="error">
									Delete Bonus
								</Button>
							)}
							<Button size="small" onClick={handleBonusSave} variant="contained">
								{nurseInShift.bonus ? "Update" : "Add"}
							</Button>
						</Box>
					</Box>
				</Menu>
				<AddDayOffDialog
					open={addPtoOpen}
					nurseId={nurseInShift.nurseId}
					onClose={handlePtoClose}
					onSave={handlePtoSave}
					initialStartDate={getShiftDate(weekIndex, shiftNo, dayNo)}
					initialEndDate={getShiftDate(weekIndex, shiftNo, dayNo)}
					/*Take the diff between start and end of shift*/
					initialHours={Math.max(1, Math.round(Math.abs(nurseInShift.endTime.diff(nurseInShift.startTime, "hours").hours)))}
				/>
				<PostNoteDialog
					open={postNoteOpen}
					nurseInShift={nurseInShift}
					shiftNo={shiftNo}
					dayNo={dayNo}
					weekIndex={weekIndex}
					showActual={showActual}
					onClose={handlePostNoteClose}
					onSave={handlePostNoteSave}
				/>
			</div>
		</ClickAwayListener>
	);
};
